// packages/ui/src/tools/ToolPanel.tsx
import {
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { X } from 'lucide-react';
import type { PlatformBridge, ToolFocusHandle, ToolHost } from '@slate/core';
import { useToolsStore } from './toolsStore';
import { ToolRegistry } from './ToolRegistry';
import { ToolPanelTabs } from './ToolPanelTabs';
import { InstanceHost } from './InstanceHost';
import { terminalSessionRegistry } from './terminal/sessionRegistry';
import { Modal } from '../components/Modal';
import { useRegisterFocusZone } from '../focus/useRegisterFocusZone';

export interface ToolPanelProps {
  platform: PlatformBridge;
  host: ToolHost;
}

const RESIZE_HIT = 6;

export function ToolPanel({ platform, host }: ToolPanelProps): ReactElement | null {
  const panel = useToolsStore((s) => s.panel);
  const instances = useToolsStore((s) => s.instances);
  const activeId = useToolsStore((s) => s.activeInstanceId);
  const showPanel = useToolsStore((s) => s.showPanel);
  const hidePanel = useToolsStore((s) => s.hidePanel);
  const setPanelHeight = useToolsStore((s) => s.setPanelHeight);

  // Re-derive the available tool list when the registry changes. Without this
  // subscription, the panel mounted before App's terminal-registration effect
  // ran would cache an empty list forever (breaking the + button).
  const [available, setAvailable] = useState<string[]>(
    () => ToolRegistry.getAvailable(platform).map((d) => d.id),
  );
  useEffect(() => {
    const sync = () => setAvailable(ToolRegistry.getAvailable(platform).map((d) => d.id));
    sync();
    return ToolRegistry.subscribe(sync);
  }, [platform]);

  const rootRef = useRef<HTMLDivElement>(null);
  /**
   * Per-instance focus handles. Each InstanceHost is permanently bound to its
   * own ref via getFocusRef(instance.id) — so when the active tab flips, we
   * can look up the new active's `focusTool()` and pull focus into it, even
   * for the previously-inactive tabs. A single shared ref would have only
   * tracked whichever tab was active at mount time.
   */
  const focusHandlesRef = useRef<Map<string, RefObject<ToolFocusHandle | null>>>(
    new Map(),
  );
  const getFocusRef = (id: string): RefObject<ToolFocusHandle | null> => {
    let ref = focusHandlesRef.current.get(id);
    if (!ref) {
      ref = { current: null };
      focusHandlesRef.current.set(id, ref);
    }
    return ref;
  };

  // Drop refs for closed instances so the map doesn't leak.
  useEffect(() => {
    const live = new Set(instances.map((i) => i.id));
    for (const id of Array.from(focusHandlesRef.current.keys())) {
      if (!live.has(id)) focusHandlesRef.current.delete(id);
    }
  }, [instances]);

  // Whenever the active instance changes (or the panel becomes visible), pull
  // focus into the now-active tool. Skip when an input inside the panel
  // already owns focus — that's the rename input mid-edit, and stealing focus
  // would cancel the rename. The rAF defers to the next paint so the
  // visibility flip lands before xterm tries to focus its hidden DOM.
  useEffect(() => {
    if (!panel.visible || !activeId) return;
    const raf = requestAnimationFrame(() => {
      if (typeof document !== 'undefined') {
        const ae = document.activeElement;
        if (ae && rootRef.current?.contains(ae) && ae.tagName === 'INPUT') return;
      }
      focusHandlesRef.current.get(activeId)?.current?.focusTool();
    });
    return () => cancelAnimationFrame(raf);
  }, [activeId, panel.visible]);

  useRegisterFocusZone(rootRef, 'tools', '.mp-terminal,[data-focus-primary]', {
    label: 'Bottom tool panel',
    order: 30,
    isVisible: () => useToolsStore.getState().panel.visible,
    // The container (`.mp-terminal`) isn't focusable and isn't the xterm input,
    // so the default selector `.focus()` is a no-op. Delegate to the ACTIVE
    // tool's imperative handle (xterm.focus()) instead — this is what makes
    // ⌥⌘↓ land in the terminal when the panel is already open. (When the panel
    // was hidden, the visibility-change effect above also re-focuses it once the
    // DOM is shown, covering the case where this fires before the re-render.)
    focus: () => {
      const id = useToolsStore.getState().activeInstanceId;
      if (id) focusHandlesRef.current.get(id)?.current?.focusTool();
    },
  });

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const startH = panel.height;
    const onMove = (m: PointerEvent) => {
      const dy = startY - m.clientY;
      setPanelHeight(startH + dy);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Panel close (X). If any tool instances are open, confirm; busy terminals
  // are highlighted. On confirm: kill every terminal session, close every
  // instance, hide the panel. With no instances, just hide.
  const [confirmingPanelClose, setConfirmingPanelClose] = useState(false);
  const busyCount = instances.filter((i) => i.toolId === 'terminal' && i.busy).length;
  const handleCloseX = () => {
    if (instances.length === 0) {
      hidePanel();
      return;
    }
    setConfirmingPanelClose(true);
  };
  const confirmClosePanel = async () => {
    await terminalSessionRegistry.killAll();
    useToolsStore.getState().closeAll();
    setConfirmingPanelClose(false);
    hidePanel();
  };

  // Only bail out when the panel is hidden AND there's nothing to preserve.
  // While instances are open we keep the panel MOUNTED and merely collapse it
  // with `display: none` — unmounting would tear down every InstanceHost /
  // TerminalView, whose effect cleanup runs `session.kill()` and destroys the
  // PTY. A hidden-but-mounted panel keeps the shells alive; toggling back just
  // reveals them (TerminalView's ResizeObserver re-fits as the box regrows).
  if (!panel.visible && instances.length === 0) return null;


  return (
    <div
      className="mp-tool-panel"
      data-focus-zone="tools"
      role="region"
      aria-label="Tool panel"
      aria-hidden={!panel.visible || undefined}
      ref={rootRef}
      style={{ height: panel.height, flex: '0 0 auto', display: panel.visible ? 'flex' : 'none', flexDirection: 'column', minHeight: 0 }}
    >
      <div
        className="mp-tool-panel__resize"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize tool panel"
        onPointerDown={startResize}
        style={{ height: RESIZE_HIT, cursor: 'ns-resize', flex: '0 0 auto' }}
      />
      <div className="mp-tool-panel__header" style={{ flex: '0 0 auto', display: 'flex' }}>
        <ToolPanelTabs
          availableToolIds={available}
          onNewTool={(toolId) => {
            useToolsStore.getState().openTool(toolId);
            showPanel();
          }}
        />
        <div className="mp-tool-panel__header-actions" style={{ marginLeft: 'auto', display: 'flex' }}>
          <button
            type="button"
            className="mp-icon-btn"
            aria-label="Close tool panel"
            title="Close tool panel"
            onClick={handleCloseX}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      </div>
      <div
        className="mp-tool-panel__body"
        style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', position: 'relative' }}
      >
        {instances.length === 0 ? (
          <EmptyState available={available} onPick={(id) => useToolsStore.getState().openTool(id)} />
        ) : (
          // Mount every instance and only toggle visibility. Unmounting the
          // inactive tab would kill its PTY and dispose its xterm; using
          // `display: none` collapses the box to 0x0 which forces xterm's
          // FitAddon to re-measure on re-show (the visible "width snap" the
          // user sees). `visibility: hidden` keeps the geometry stable so
          // switching tabs is instant and silent.
          instances.map((instance) => {
            const def = ToolRegistry.getById(instance.toolId);
            if (!def) return null;
            const isActive = instance.id === activeId;
            return (
              <div
                key={instance.id}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  visibility: isActive ? 'visible' : 'hidden',
                  pointerEvents: isActive ? 'auto' : 'none',
                  zIndex: isActive ? 1 : 0,
                }}
                aria-hidden={!isActive}
              >
                <InstanceHost
                  instance={instance}
                  def={def}
                  host={host}
                  focusToolRef={getFocusRef(instance.id)}
                />
              </div>
            );
          })
        )}
      </div>
      {confirmingPanelClose && (
        <Modal
          open
          onClose={() => setConfirmingPanelClose(false)}
          title="Close tool panel?"
          footer={
            <>
              <button
                type="button"
                className="mp-btn"
                onClick={() => setConfirmingPanelClose(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="mp-btn mp-btn--primary"
                onClick={() => { void confirmClosePanel(); }}
              >
                Close anyway
              </button>
            </>
          }
        >
          <p className="mp-modal__text">
            {instances.length === 1
              ? 'This will close 1 open tool.'
              : `This will close ${instances.length} open tools.`}
            {busyCount > 0 && (
              <>
                {' '}
                {busyCount === 1
                  ? '1 terminal has a running process and will be sent SIGHUP.'
                  : `${busyCount} terminals have running processes and will be sent SIGHUP.`}
              </>
            )}
          </p>
        </Modal>
      )}
    </div>
  );
}

function EmptyState({ available, onPick }: { available: string[]; onPick: (id: string) => void }) {
  if (available.length === 0) {
    return <div className="mp-tool-empty">No tools available.</div>;
  }
  return (
    <div className="mp-tool-empty">
      {available.map((id) => {
        const def = ToolRegistry.getById(id);
        return (
          <button
            key={id}
            type="button"
            className="mp-btn mp-btn--primary"
            onClick={() => onPick(id)}
          >
            New {def?.title ?? id}
          </button>
        );
      })}
    </div>
  );
}
